'use client';

import { COLORS, PATHS } from '@/constants';
import {
  CastleIcon,
  DateCalendarIcon,
  GlobeIcon,
  MailIcon,
  MapPinIcon,
  PenIcon,
  PhoneIcon,
} from '@/components';
import { Button, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { FC } from 'react';
import { useRouter } from 'next/navigation';
import { useGetOfficeProfile } from '@/queris';
import { useImageObserver } from '@/hooks';
import { sanitizeRichHtml } from '@/common/helpers';
import { toTelHref } from '@/utils/formatPhone';
import { runtimeConfig } from '@/lib';
import { BrokerActiveLocations } from './components/BrokerActiveLocations';
import { BrokerProfileSkeleton } from './components/BrokerProfileSkeleton';
import { BrokerProfilePrices } from './components/BrokerProfilePrices';
import {
  AboutText,
  CompanyLogo,
  CompanyNameRow,
  ContactIconBox,
  ContactItem,
  ContactLabel,
  ContactLink,
  ContactTextBlock,
  ContactValue,
  ContactsGrid,
  Container,
  ContentCard,
  HeroBanner,
  Section,
  SectionTitle,
} from './styled';

const ICON_COLOR = COLORS['primary/700'];

export const BrokerProfileScreen: FC = () => {
  const t = useTranslations('memberProfile');
  const router = useRouter();

  const { data: profile, isLoading } = useGetOfficeProfile();
  const { imgSrc: bigLogoSrc, handleError: onBigLogoError } = useImageObserver(
    profile?.bigLogo,
    true,
  );
  const { imgSrc: smallLogoSrc, handleError: onSmallLogoError } = useImageObserver(
    profile?.smallLogo,
    true,
  );

  const handleEdit = () => {
    router.push(`/profile/${PATHS.MEMBER_PROFILE}/edit`);
  };

  if (isLoading) {
    return <BrokerProfileSkeleton />;
  }

  return (
    <Container>
      <Typography
        fontSize={30}
        fontWeight={700}
        color={COLORS['primary/900']}
        lineHeight={1}
        marginBottom="22px"
      >
        {t('title')}
      </Typography>

      <ContentCard>
        <HeroBanner>
          <img
            key={bigLogoSrc ?? 'hero'}
            src={bigLogoSrc ?? undefined}
            alt="hero"
            onError={onBigLogoError}
          />
        </HeroBanner>

        <Section>
          <CompanyNameRow>
            <CompanyLogo>
              <img
                key={smallLogoSrc ?? 'logo'}
                src={smallLogoSrc ?? undefined}
                alt="logo"
                onError={onSmallLogoError}
              />
            </CompanyLogo>
            <Typography fontSize={22} fontWeight={700} color={COLORS['primary/900']} width={'100%'}>
              {profile?.name ?? '-'}
            </Typography>
            <Button
              variant="outline-primary"
              type="button"
              onClick={handleEdit}
              sx={{
                height: '38px',
              }}
            >
              <PenIcon width={14} height={14} />
              {t('editButton')}
            </Button>
          </CompanyNameRow>
        </Section>

        {profile?.aboutUs && (
          <Section>
            <SectionTitle>{t('aboutUs.title')}</SectionTitle>
            <AboutText dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(profile.aboutUs) }} />
          </Section>
        )}

        <Section>
          <SectionTitle>{t('profileLink.label')}</SectionTitle>
          <ContactItem>
            <ContactIconBox>
              <GlobeIcon width={24} height={24} color={ICON_COLOR} />
            </ContactIconBox>
            <ContactTextBlock>
              <ContactLabel>{t('profileLink.directLink')}</ContactLabel>
              <ContactValue>
                {profile?.directLink ? (
                  <a
                    href={`${runtimeConfig.BASE_URL ?? ''}/${PATHS.BROKERS}/${profile.directLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {`${runtimeConfig.BASE_URL ?? ''}/${PATHS.BROKERS}/${profile.directLink}`}
                  </a>
                ) : (
                  '—'
                )}
              </ContactValue>
            </ContactTextBlock>
          </ContactItem>
        </Section>

        <Section>
          <SectionTitle>{t('contacts.title')}</SectionTitle>
          <ContactsGrid>
            <ContactItem>
              <ContactIconBox>
                <MailIcon width={24} height={24} color={ICON_COLOR} />
              </ContactIconBox>
              <ContactTextBlock>
                <ContactLabel>{t('contacts.email')}</ContactLabel>
                {profile?.email ? (
                  <ContactLink href={`mailto:${profile.email}`}>{profile.email}</ContactLink>
                ) : (
                  <ContactValue>—</ContactValue>
                )}
              </ContactTextBlock>
            </ContactItem>
            <ContactItem>
              <ContactIconBox>
                <PhoneIcon width={24} height={24} color={ICON_COLOR} />
              </ContactIconBox>
              <ContactTextBlock>
                <ContactLabel>{t('contacts.phone')}</ContactLabel>
                {profile?.phone ? (
                  <ContactLink href={`tel:${toTelHref(profile.phone)}`}>
                    {profile.phone}
                  </ContactLink>
                ) : (
                  <ContactValue>—</ContactValue>
                )}
              </ContactTextBlock>
            </ContactItem>
            <ContactItem>
              <ContactIconBox>
                <GlobeIcon width={24} height={24} />
              </ContactIconBox>
              <ContactTextBlock>
                <ContactLabel>{t('contacts.website')}</ContactLabel>
                {profile?.site ? (
                  <ContactLink href={profile.site} target="_blank" rel="noopener noreferrer">
                    {profile.site}
                  </ContactLink>
                ) : (
                  <ContactValue>—</ContactValue>
                )}
              </ContactTextBlock>
            </ContactItem>
            <ContactItem>
              <ContactIconBox>
                <MapPinIcon width={24} height={24} />
              </ContactIconBox>
              <ContactTextBlock>
                <ContactLabel>{t('contacts.address')}</ContactLabel>
                <ContactValue>{profile?.address || '—'}</ContactValue>
              </ContactTextBlock>
            </ContactItem>
            <ContactItem>
              <ContactIconBox>
                <DateCalendarIcon width={24} height={24} />
              </ContactIconBox>
              <ContactTextBlock>
                <ContactLabel>{t('contacts.founded')}</ContactLabel>
                <ContactValue>{profile?.founded || '—'}</ContactValue>
              </ContactTextBlock>
            </ContactItem>
            <ContactItem>
              <ContactIconBox>
                <CastleIcon width={24} height={24} />
              </ContactIconBox>
              <ContactTextBlock>
                <ContactLabel>{t('contacts.regions')}</ContactLabel>
                <ContactValue>{profile?.region || '—'}</ContactValue>
              </ContactTextBlock>
            </ContactItem>
          </ContactsGrid>
        </Section>

        <BrokerActiveLocations activeLocations={profile?.activeLocations} />

        <BrokerProfilePrices directLink={profile?.directLink} />
      </ContentCard>
    </Container>
  );
};
